import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Table } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { pinMetaServiceQa, pinStatusServiceQa, pinValidateServiceQa } from '../../../utils/pins_services';
import './PinsEstado.css';

const normalizeType = (value) => {
  const raw = (value || '').toString().trim().toLowerCase();
  if (raw === '1' || raw === 'serial') return 'serial';
  if (raw === '2' || raw === 'pin') return 'pin';
  if (raw === '3' || raw === 'lot' || raw === 'lote') return 'lot';
  if (raw === '4' || raw === 'email') return 'email';
  return 'serial';
};

const typeOptions = [
  { value: 'serial', label: 'Serial' },
  { value: 'pin', label: 'PIN' },
  { value: 'lot', label: 'Lote' },
  { value: 'email', label: 'Email' }
];

const getTypeLabel = (type) => {
  switch (type) {
    case 'pin':
      return 'PIN del producto';
    case 'lot':
      return 'Número de Lote';
    case 'email':
      return 'Email';
    default:
      return 'Serial del producto';
  }
};

function PinsEstadoQA({ usr_id, permission, token }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionToken = token || localStorage.getItem('token');
  const permissionLevel = Number(permission || 0);
  const isReadOnly = permissionLevel !== 3;

  const [type, setType] = useState(normalizeType(searchParams.get('type')));
  const [identify, setIdentify] = useState(searchParams.get('identify') || '');
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '0', 10));

  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [meta, setMeta] = useState({
    statusTypes: [],
    timeTypes: [],
    pinTypes: []
  });
  const [metaLoading, setMetaLoading] = useState(false);
  const [selectedTimeTypeId, setSelectedTimeTypeId] = useState('');
  const [selectedPinTypeId, setSelectedPinTypeId] = useState('');
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [errorMessage, setErrorMessage] = useState('');
  const [validating, setValidating] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showConfirmLot, setShowConfirmLot] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [searchMode, setSearchMode] = useState('text');
  const [csvKind, setCsvKind] = useState('pin');
  const [csvResultActive, setCsvResultActive] = useState(false);
  const csvFileInputRef = useRef(null);

  useEffect(() => {
    if (permissionLevel !== 3) {
      navigate('/pins/estado');
      return;
    }
  }, [permissionLevel, navigate]);

  useEffect(() => {
    const loadMeta = async () => {
      if (permissionLevel !== 3) {
        return;
      }
      if (!sessionToken) {
        navigate('/');
        return;
      }
      setMetaLoading(true);
      try {
        const response = await pinMetaServiceQa.getMeta(sessionToken);
        if (response === 'INVALID') {
          navigate('/');
          return;
        }
        setMeta((current) => ({
          ...current,
          timeTypes: response?.timeTypes || [],
          pinTypes: response?.pinTypes || []
        }));
      } catch (error) {
        console.error('Error al cargar metadatos de PIN:', error);
      } finally {
        setMetaLoading(false);
      }
    };

    loadMeta();
  }, [navigate, sessionToken, permissionLevel]);

  useEffect(() => {
    if (searchMode === 'csv') {
      const nextType = normalizeType(searchParams.get('type'));
      const nextIdentify = searchParams.get('identify') || '';
      const nextPage = parseInt(searchParams.get('page') || '0', 10);
      setType(nextType);
      setIdentify(nextIdentify);
      setPage(nextPage);
      return;
    }
    const nextType = normalizeType(searchParams.get('type'));
    const nextIdentify = searchParams.get('identify') || '';
    const nextPage = parseInt(searchParams.get('page') || '0', 10);
    setType(nextType);
    setIdentify(nextIdentify);
    setPage(nextPage);
    if (nextIdentify) {
      runSearch(nextType, nextIdentify, nextPage);
    }
  }, [searchParams, searchMode]);

  const mapById = (list) => {
    const map = new Map();
    (list || []).forEach((item) => {
      if (item && item.id !== undefined) {
        map.set(String(item.id), item.name || item.label || item.title || item.description || '');
      }
    });
    return map;
  };

  const statusMap = useMemo(() => mapById(meta.statusTypes), [meta.statusTypes]);
  const timeMap = useMemo(() => mapById(meta.timeTypes), [meta.timeTypes]);
  const pinTypeMap = useMemo(() => mapById(meta.pinTypes), [meta.pinTypes]);

  const getUserName = (user) => {
    if (!user || !user.person) return 'N/A';
    const first = user.person.first_name || '';
    const last = user.person.last_name || '';
    const full = `${first} ${last}`.trim();
    return full || 'N/A';
  };

  const formatDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    return date.toLocaleString();
  };

  const updateSearchParams = (nextType, nextIdentify, nextPage = 0) => {
    const params = {
      type: nextType,
      identify: nextIdentify
    };
    if (nextType === 'lot' && nextPage) {
      params.page = String(nextPage);
    }
    setSearchParams(params);
  };

  const runSearch = async (nextType = type, nextIdentify = identify, nextPage = page) => {
    if (permissionLevel !== 3) {
      navigate('/pins/estado');
      return;
    }
    if (!sessionToken) {
      navigate('/');
      return;
    }
    if (!nextIdentify) {
      setResults([]);
      setErrorMessage('Ingresa un valor para buscar.');
      return;
    }

    setCsvResultActive(false);

    setLoading(true);
    setErrorMessage('');
    try {
      const response = await pinStatusServiceQa.search(nextType, nextIdentify, nextPage, sessionToken);
      if (response?.error === 'PinStatusServiceError') {
        setResults([]);
        setSelectedKeys(new Set());
        setErrorMessage('No se encontró datos.');
        return;
      }
      if (response === 'INVALID') {
        navigate('/');
        return;
      }
      setResults(response?.results || []);
      setSelectedKeys(new Set());
      setMeta(response?.meta || { statusTypes: [], timeTypes: [], pinTypes: [] });
      setPage(response?.page ?? nextPage);
    } catch (error) {
      console.error('Error al buscar estado de PIN:', error);
      setResults([]);
      setErrorMessage('No se pudo completar la búsqueda.');
    } finally {
      setLoading(false);
    }
  };

  const runCsvBatchSearch = async (file) => {
    if (permissionLevel !== 3) {
      navigate('/pins/estado');
      return;
    }
    if (!sessionToken) {
      navigate('/');
      return;
    }
    if (!file) {
      setErrorMessage('Selecciona un archivo CSV o TXT.');
      return;
    }

    setLoading(true);
    setErrorMessage('');
    try {
      const response = await pinStatusServiceQa.searchCsvBatch(file, csvKind, sessionToken);
      if (response?.error === 'PinBatchStatusServiceError') {
        setResults([]);
        setSelectedKeys(new Set());
        setErrorMessage(response?.message || 'Error en la consulta por archivo.');
        return;
      }
      if (response?.error === 'MissingFile' || response?.error === 'InvalidFile' || response?.error === 'MissingParameters') {
        setResults([]);
        setSelectedKeys(new Set());
        setErrorMessage(response?.message || 'No se pudo procesar el archivo.');
        return;
      }
      if (response === 'INVALID') {
        navigate('/');
        return;
      }
      setCsvResultActive(true);
      setResults(response?.results || []);
      setSelectedKeys(new Set());
      setMeta(response?.meta || { statusTypes: [], timeTypes: [], pinTypes: [] });
      setPage(0);
    } catch (error) {
      console.error('Error al consultar CSV:', error);
      setResults([]);
      setErrorMessage('No se pudo completar la consulta por archivo.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchModeChange = (mode) => {
    setSearchMode(mode);
    setErrorMessage('');
    setValidationError('');
    if (mode === 'csv') {
      setCsvResultActive(false);
      setCsvKind('pin');
      setSearchParams((prev) => {
        const p = new URLSearchParams(prev);
        p.set('type', 'pin');
        p.delete('identify');
        p.delete('page');
        return p;
      });
      setType('pin');
      setIdentify('');
      setPage(0);
      setResults([]);
      setSelectedKeys(new Set());
    } else {
      setCsvResultActive(false);
    }
  };

  const handleCsvSearchClick = () => {
    const file = csvFileInputRef.current?.files?.[0];
    runCsvBatchSearch(file);
  };

  const refreshAfterValidate = async () => {
    if (csvResultActive) {
      const f = csvFileInputRef.current?.files?.[0];
      if (f) {
        await runCsvBatchSearch(f);
        return;
      }
    }
    if (identify) {
      await runSearch(type, identify, page);
    }
  };

  const handleSearch = () => {
    const nextPage = type === 'lot' ? 0 : 0;
    updateSearchParams(type, identify, nextPage);
  };

  const handleTypeChange = (value) => {
    setType(value);
    setResults([]);
    setErrorMessage('');
    setSelectedKeys(new Set());
    setCsvResultActive(false);
  };

  const handlePageChange = (nextPage) => {
    updateSearchParams(type, identify, nextPage);
  };

  const lotInfo = results?.[0] || null;
  const lotPagination = lotInfo?.pinPagination || null;
  const lotPins = lotPagination?.data || [];
  const totalPages = lotPagination?.last_page || 0;
  const visiblePins = type === 'lot' && !csvResultActive ? lotPins : results;

  const getMetaLabel = (item) => {
    return item?.name || item?.label || item?.title || item?.description || item?.id || '';
  };

  const getPinKey = (pin) => {
    if (!pin) return '';
    return String(pin.id ?? pin.identify ?? pin.serial?.identify ?? '');
  };

  const allSelectableKeys = visiblePins.map(getPinKey).filter(Boolean);
  const isAllSelected = allSelectableKeys.length > 0
    && allSelectableKeys.every((key) => selectedKeys.has(key));

  const handleToggleSelectAll = () => {
    if (!allSelectableKeys.length) return;
    if (isAllSelected) {
      const next = new Set(selectedKeys);
      allSelectableKeys.forEach((key) => next.delete(key));
      setSelectedKeys(next);
      return;
    }
    const next = new Set(selectedKeys);
    allSelectableKeys.forEach((key) => next.add(key));
    setSelectedKeys(next);
  };

  const handleToggleSelect = (key) => {
    if (!key) return;
    const next = new Set(selectedKeys);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    setSelectedKeys(next);
  };

  const buildSerialList = () => {
    return visiblePins
      .filter((pin) => selectedKeys.has(getPinKey(pin)))
      .map((pin) => {
        const serial = pin.serial || pin;
        if (!serial?.id) {
          return null;
        }
        return {
          id: serial.id,
          identify: serial.identify || pin.identify || ''
        };
      })
      .filter(Boolean);
  };

  const getValidatePayload = () => {
    const time_type_id = selectedTimeTypeId;
    const pin_type_id = selectedPinTypeId;

    return {
      time_type_id,
      pin_type_id,
      serials: buildSerialList()
    };
  };

  const getValidateLotPayload = () => {
    const time_type_id = selectedTimeTypeId;
    const pin_type_id = selectedPinTypeId;
    const lot_id = lotInfo?.id || null;

    if (!lot_id) {
      return null;
    }

    return {
      time_type_id,
      pin_type_id,
      lot_id
    };
  };

  const handleValidate = () => {
    if (isReadOnly) {
      setValidationError('No tienes permisos para validar PINes.');
      return;
    }
    setValidationError('');
    if (!selectedKeys.size) {
      setValidationError('Selecciona al menos un PIN.');
      return;
    }
    if (!selectedTimeTypeId || !selectedPinTypeId) {
      setValidationError('Selecciona vigencia y tipo de producto.');
      return;
    }
    setShowConfirm(true);
  };

  const handleValidateLot = () => {
    if (isReadOnly) {
      setValidationError('No tienes permisos para validar PINes.');
      return;
    }
    setValidationError('');
    if (!selectedTimeTypeId || !selectedPinTypeId) {
      setValidationError('Selecciona vigencia y tipo de producto.');
      return;
    }
    if (!lotInfo?.id) {
      setValidationError('No se pudo obtener la información del lote.');
      return;
    }
    setShowConfirmLot(true);
  };

  const confirmValidate = async () => {
    if (!sessionToken) {
      navigate('/');
      return;
    }
    if (isReadOnly) {
      setValidationError('No tienes permisos para validar PINes.');
      return;
    }

    const payload = getValidatePayload();
    if (!payload) {
      setValidationError('Datos incompletos para validar.');
      return;
    }

    setValidating(true);
    try {
      const response = await pinValidateServiceQa.activate(payload, sessionToken);
      if (response === 'INVALID') {
        navigate('/');
        return;
      }
      setShowConfirm(false);
      setSelectedKeys(new Set());
      await refreshAfterValidate();
    } catch (error) {
      console.error('Error al validar PINes:', error);
      setValidationError('No se pudo validar los PINes.');
    } finally {
      setValidating(false);
    }
  };

  const confirmValidateLot = async () => {
    if (!sessionToken) {
      navigate('/');
      return;
    }
    if (isReadOnly) {
      setValidationError('No tienes permisos para validar PINes.');
      return;
    }

    const payload = getValidateLotPayload();
    if (!payload) {
      setValidationError('Datos incompletos para validar el lote.');
      return;
    }

    setValidating(true);
    try {
      const response = await pinValidateServiceQa.activate(payload, sessionToken);
      if (response === 'INVALID') {
        navigate('/');
        return;
      }
      setShowConfirmLot(false);
      setSelectedKeys(new Set());
      await refreshAfterValidate();
    } catch (error) {
      console.error('Error al validar lote:', error);
      setValidationError('No se pudo validar el lote completo.');
    } finally {
      setValidating(false);
    }
  };

  return (
    <div className="pins-estado-container">
      <h1 className="pins-estado-title">ESTADO DE PIN QA</h1>

      <div className="pins-estado-filters">
        <div className="pins-estado-mode-group" role="radiogroup" aria-label="Modo de búsqueda">
          <label className="pins-estado-radio">
            <input
              type="radio"
              name="pins-estado-search-mode-qa"
              value="text"
              checked={searchMode === 'text'}
              onChange={() => handleSearchModeChange('text')}
            />
            <span>Búsqueda por texto</span>
          </label>
          <label className="pins-estado-radio">
            <input
              type="radio"
              name="pins-estado-search-mode-qa"
              value="csv"
              checked={searchMode === 'csv'}
              onChange={() => handleSearchModeChange('csv')}
            />
            <span>Búsqueda por CSV / TXT</span>
          </label>
        </div>

        {searchMode === 'text' ? (
          <>
            <div className="pins-estado-type-group">
              {typeOptions.map((option) => (
                <label key={option.value} className="pins-estado-radio">
                  <input
                    type="radio"
                    name="pins-estado-type"
                    value={option.value}
                    checked={type === option.value}
                    onChange={() => handleTypeChange(option.value)}
                  />
                  <span>{option.label}</span>
                </label>
              ))}
            </div>

            <div className="pins-estado-search">
              <input
                type="text"
                placeholder={getTypeLabel(type)}
                value={identify}
                onChange={(event) => setIdentify(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    handleSearch();
                  }
                }}
              />
              <button type="button" className="pins-estado-button" onClick={handleSearch}>
                Buscar
              </button>
            </div>
          </>
        ) : (
          <div className="pins-estado-csv-panel">
            <p className="pins-estado-csv-hint">
              Un valor por línea (UTF-8). Sin cabecera salvo que la API lo permita. Luego elige si las líneas son
              identificadores de PIN o de serial.
            </p>
            <div className="pins-estado-csv-kind" role="radiogroup" aria-label="Contenido del archivo">
              <label className="pins-estado-radio">
                <input
                  type="radio"
                  name="pins-estado-csv-kind-qa"
                  value="pin"
                  checked={csvKind === 'pin'}
                  onChange={() => setCsvKind('pin')}
                />
                <span>Archivo con PINes</span>
              </label>
              <label className="pins-estado-radio">
                <input
                  type="radio"
                  name="pins-estado-csv-kind-qa"
                  value="serial"
                  checked={csvKind === 'serial'}
                  onChange={() => setCsvKind('serial')}
                />
                <span>Archivo con seriales</span>
              </label>
            </div>
            <div className="pins-estado-csv-actions">
              <input
                ref={csvFileInputRef}
                type="file"
                accept=".csv,.txt,text/plain"
                className="pins-estado-csv-file"
              />
              <button type="button" className="pins-estado-button" onClick={handleCsvSearchClick}>
                Consultar archivo
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="pins-estado-meta">
        <label className="pins-estado-select-all">
          <input
            type="checkbox"
            checked={isAllSelected}
            onChange={handleToggleSelectAll}
            disabled={!allSelectableKeys.length || isReadOnly}
          />
          Seleccionar todo
        </label>

        <span className="pins-estado-selected-count">
          Seleccionados: {selectedKeys.size}
        </span>

        {!isReadOnly && (selectedKeys.size > 0 || (type === 'lot' && lotInfo && !csvResultActive)) && (
          <>
            <select
              value={selectedTimeTypeId}
              onChange={(event) => setSelectedTimeTypeId(event.target.value)}
              disabled={metaLoading}
            >
              <option value="">
                {metaLoading ? 'Cargando vigencias...' : 'Selecciona vigencia'}
              </option>
              {meta.timeTypes.map((item) => (
                <option key={item.id ?? getMetaLabel(item)} value={item.id}>
                  {getMetaLabel(item)}
                </option>
              ))}
            </select>

            <select
              value={selectedPinTypeId}
              onChange={(event) => setSelectedPinTypeId(event.target.value)}
              disabled={metaLoading}
            >
              <option value="">
                {metaLoading ? 'Cargando tipos...' : 'Tipo de producto'}
              </option>
              {meta.pinTypes.map((item) => (
                <option key={item.id ?? getMetaLabel(item)} value={item.id}>
                  {getMetaLabel(item)}
                </option>
              ))}
            </select>

            {selectedKeys.size > 0 && (
              <button
                type="button"
                className="pins-estado-button"
                onClick={handleValidate}
                disabled={metaLoading || validating}
              >
                Activar Seleccionados
              </button>
            )}

            {type === 'lot' && lotInfo && !csvResultActive && (
              <button
                type="button"
                className="pins-estado-button pins-estado-button--lot"
                onClick={handleValidateLot}
                disabled={metaLoading || validating}
              >
                Validar Todo el Lote
              </button>
            )}
          </>
        )}
      </div>

      {validationError && (
        <div className="pins-estado-empty">{validationError}</div>
      )}

      {type === 'lot' && lotInfo && !csvResultActive && (
        <div className="pins-estado-lot-info">
          <span>Cantidad: {lotPagination?.total ?? 'N/A'}</span>
          <span>Producto: {lotPins?.[0]?.serial?.product?.name ?? 'N/A'}</span>
          <span>Número de Lote: {lotInfo.identify ?? 'N/A'}</span>
          <span>Fecha: {formatDate(lotInfo.created_at)}</span>
          <span>Responsable: {getUserName(lotInfo.user)}</span>
        </div>
      )}

      {loading && <div className="pins-estado-loading">Buscando...</div>}
      {errorMessage && !loading && <div className="pins-estado-empty">{errorMessage}</div>}

      {!loading && !errorMessage && (type !== 'lot' || csvResultActive) && (
        <div className="pins-estado-table">
          <Table hover responsive>
            <thead>
              <tr>
                <th></th>
                <th>#</th>
                <th>Estado</th>
                <th>User ID</th>
                <th>Nombre de usuario</th>
                <th>Correo electrónico</th>
                <th>Teléfono</th>
                <th>Producto</th>
                <th>PIN</th>
                <th>Serial</th>
                <th>Vigencia</th>
                <th>Fecha activación</th>
              </tr>
            </thead>
            <tbody>
              {results.length === 0 ? (
                <tr>
                  <td colSpan="12" className="text-center py-4">
                    {csvResultActive
                      ? (csvKind === 'serial'
                        ? 'No hay PIN asociado a los seriales del archivo.'
                        : 'Ningún PIN encontrado para las líneas del archivo.')
                      : (type === 'serial' ? 'Serial no encontrado' : 'PIN no encontrado')}
                  </td>
                </tr>
              ) : (
                results.map((pin, index) => (
                  <tr key={`${pin.identify || index}`}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedKeys.has(getPinKey(pin))}
                        onChange={() => handleToggleSelect(getPinKey(pin))}
                        disabled={isReadOnly}
                      />
                    </td>
                    <td>{index + 1}</td>
                    <td>{statusMap.get(String(pin.status_type_id)) || pin.status_type_id || 'N/A'}</td>
                    <td>{pin.user_id || 'N/A'}</td>
                    <td>{getUserName(pin.user)}</td>
                    <td>{pin.user?.email || 'N/A'}</td>
                    <td>{pin.user?.person?.phone || 'N/A'}</td>
                    <td>
                      {pin.serial?.product?.name || 'N/A'} :{' '}
                      {pinTypeMap.get(String(pin.pin_type_id)) || pin.pin_type_id || 'N/A'}
                    </td>
                    <td>{pin.identify || 'N/A'}</td>
                    <td>{pin.serial?.identify || 'N/A'}</td>
                    <td>{timeMap.get(String(pin.time_type_id)) || pin.time_type_id || 'N/A'}</td>
                    <td>{formatDate(pin.actived_at)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      )}

      {!loading && !errorMessage && type === 'lot' && !csvResultActive && (
        <div className="pins-estado-table">
          <Table hover responsive>
            <thead>
              <tr>
                <th></th>
                <th>#</th>
                <th>Estado</th>
                <th>User ID</th>
                <th>Nombre de usuario</th>
                <th>Correo electrónico</th>
                <th>Teléfono</th>
                <th>Producto</th>
                <th>PIN</th>
                <th>Serial</th>
                <th>Vigencia</th>
                <th>Fecha activación</th>
              </tr>
            </thead>
            <tbody>
              {lotPins.length === 0 ? (
                <tr>
                  <td colSpan="12" className="text-center py-4">
                    Lote no encontrado
                  </td>
                </tr>
              ) : (
                lotPins.map((pin, index) => (
                  <tr key={`${pin.identify || index}`}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedKeys.has(getPinKey(pin))}
                        onChange={() => handleToggleSelect(getPinKey(pin))}
                        disabled={isReadOnly}
                      />
                    </td>
                    <td>{((lotPagination?.current_page || 1) - 1) * 20 + index + 1}</td>
                    <td>{statusMap.get(String(pin.status_type_id)) || pin.status_type_id || 'N/A'}</td>
                    <td>{pin.user_id || 'N/A'}</td>
                    <td>{getUserName(pin.user)}</td>
                    <td>{pin.user?.email || 'N/A'}</td>
                    <td>{pin.user?.person?.phone || 'N/A'}</td>
                    <td>
                      {pin.serial?.product?.name || 'N/A'} :{' '}
                      {pinTypeMap.get(String(pin.pin_type_id)) || pin.pin_type_id || 'N/A'}
                    </td>
                    <td>{pin.identify || 'N/A'}</td>
                    <td>{pin.serial?.identify || 'N/A'}</td>
                    <td>{timeMap.get(String(pin.time_type_id)) || pin.time_type_id || 'N/A'}</td>
                    <td>{formatDate(pin.actived_at)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      )}

      {type === 'lot' && totalPages > 1 && !csvResultActive && (
        <div className="pins-estado-pagination">
          <button
            type="button"
            className="pagination-button"
            onClick={() => handlePageChange(Math.max((lotPagination?.current_page || 1) - 1, 1))}
            disabled={(lotPagination?.current_page || 1) <= 1}
          >
            &lt;
          </button>
          <span className="pagination-info">
            Página {lotPagination?.current_page || 1} de {totalPages}
          </span>
          <button
            type="button"
            className="pagination-button"
            onClick={() => handlePageChange(Math.min((lotPagination?.current_page || 1) + 1, totalPages))}
            disabled={(lotPagination?.current_page || 1) >= totalPages}
          >
            &gt;
          </button>
        </div>
      )}

      {showConfirm && (
        <div className="pins-estado-confirm-backdrop" onClick={() => setShowConfirm(false)}>
          <div
            className="pins-estado-confirm-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="pins-estado-confirm-icon">!</div>
            <h3>¡Espera un momento!</h3>
            <p>
              Estás a punto de validar {selectedKeys.size} PINES.
              ¿Seguro que deseas habilitarlos?
            </p>
            <div className="pins-estado-confirm-actions">
              <button
                type="button"
                className="pins-estado-confirm-cancel"
                onClick={() => setShowConfirm(false)}
              >
                En otro momento
              </button>
              <button
                type="button"
                className="pins-estado-confirm-accept"
                onClick={confirmValidate}
                disabled={validating}
              >
                {validating ? 'Validando...' : 'VALIDAR'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showConfirmLot && (
        <div className="pins-estado-confirm-backdrop" onClick={() => setShowConfirmLot(false)}>
          <div
            className="pins-estado-confirm-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="pins-estado-confirm-icon">!</div>
            <h3>¡Espera un momento!</h3>
            <p>
              Estás a punto de validar TODO EL LOTE ({lotPagination?.total ?? 'N/A'} PINes).
              ¿Seguro que deseas habilitarlos todos?
            </p>
            <div className="pins-estado-confirm-actions">
              <button
                type="button"
                className="pins-estado-confirm-cancel"
                onClick={() => setShowConfirmLot(false)}
              >
                En otro momento
              </button>
              <button
                type="button"
                className="pins-estado-confirm-accept"
                onClick={confirmValidateLot}
                disabled={validating}
              >
                {validating ? 'Validando...' : 'VALIDAR TODO EL LOTE'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PinsEstadoQA;
